import { UPLOADS_URL } from "../../config/constants/api";
import { Player } from "react-tuby";
import "react-tuby/css/main.css";
import { memo, useEffect, useRef } from "react";

function VideoPlayer({ data, index }) {

  // Helper function to construct URL properly
  const constructUrl = (filePath) => {
    console.log('constructUrl - Input:', filePath);
    
    if (!filePath) {
      console.log('constructUrl - filePath is falsy');
      return null;
    }
    
    // Handle non-string types (convert to string)
    const stringPath = typeof filePath === 'string' ? filePath : String(filePath);
    const trimmedPath = stringPath.trim();
    
    if (!trimmedPath) {
      console.log('constructUrl - trimmedPath is empty');
      return null;
    }
    
    // If it's already a full URL (http, https, or blob), return as is
    if (trimmedPath.startsWith('http://') || trimmedPath.startsWith('https://') || trimmedPath.startsWith('blob:')) {
      console.log('constructUrl - Already full URL:', trimmedPath);
      return trimmedPath;
    }
    
    // If it already includes UPLOADS_URL, return as is
    if (trimmedPath.includes(UPLOADS_URL)) {
      console.log('constructUrl - Already includes UPLOADS_URL:', trimmedPath);
      return trimmedPath;
    }
    
    // Remove leading slash if present to avoid double slashes
    const cleanPath = trimmedPath.startsWith('/') ? trimmedPath.slice(1) : trimmedPath;
    const finalUrl = `${UPLOADS_URL}/${cleanPath}`;
    console.log('constructUrl - Final URL:', finalUrl);
    return finalUrl;
  };
  
  // Calculate URL first (before hooks, but these are just variables, not hooks)
  let url = null;
  if (index !== null && index !== undefined) {
    // When index is provided, use fileUrls array or fallback to fileUrl
    if (data?.fileUrls && Array.isArray(data.fileUrls) && data.fileUrls[index]) {
      url = constructUrl(data.fileUrls[index]);
    } else if (data?.fileUrl) {
      url = constructUrl(data.fileUrl);
    }
  } else {
    // When index is undefined, use fileUrl
    if (data?.fileUrl) {
      url = constructUrl(data.fileUrl);
    }
  }

  console.log('VideoPlayer - Final URL:', url);

  // Determine poster/thumbnail image (before hooks)
  let posterUrl = null;
  if (data?.vidImage) {
    const cleanVidImage = data.vidImage.startsWith('/') ? data.vidImage.slice(1) : data.vidImage;
    posterUrl = `${UPLOADS_URL}/${cleanVidImage}`;
  } else if (data?.videoThumbnails && index !== null && index !== undefined && data.videoThumbnails[index]) {
    const cleanThumbnail = data.videoThumbnails[index].startsWith('/') 
      ? data.videoThumbnails[index].slice(1) 
      : data.videoThumbnails[index];
    posterUrl = `${UPLOADS_URL}/${cleanThumbnail}`;
  } else if (data?.image) {
    const cleanImage = data.image.startsWith('/') ? data.image.slice(1) : data.image;
    posterUrl = `${UPLOADS_URL}/${cleanImage}`;
  }

  // Hooks must be called at the top level, before any conditional returns
  const playerRef = useRef(null);

  // Prevent form submission when clicking video player controls
  const handleClick = (e) => {
    e.stopPropagation();
    // Prevent any buttons inside from submitting forms
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      e.preventDefault();
    }
  };

  // Ensure all buttons inside the player have type="button" to prevent form submission
  useEffect(() => {
    // Only set button types if we have a valid URL (player will render)
    if (!url || typeof url !== 'string' || url.trim() === '') {
      return;
    }

    const setButtonTypes = () => {
      if (playerRef.current) {
        const buttons = playerRef.current.querySelectorAll('button');
        buttons.forEach((button) => {
          if (!button.getAttribute('type')) {
            button.setAttribute('type', 'button');
          }
        });
      }
    };

    // Set button types immediately
    setButtonTypes();

    // Also set after a short delay to catch dynamically created buttons
    const timeoutId = setTimeout(setButtonTypes, 100);

    // Use MutationObserver to catch buttons added dynamically
    const observer = new MutationObserver(setButtonTypes);
    if (playerRef.current) {
      observer.observe(playerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [url, posterUrl]);

  // If URL is still null or undefined, don't render the player
  // This check happens AFTER all hooks are called
  if (!url || typeof url !== 'string' || url.trim() === '') {
    console.warn('VideoPlayer: Invalid or missing video URL', { 
      fileUrl: data?.fileUrl, 
      fileUrls: data?.fileUrls, 
      index,
      url,
      dataType: typeof data?.fileUrl
    });
    // Return a debug message in development
    if (process.env.NODE_ENV === 'development') {
      return (
        <div style={{ padding: '20px', border: '2px solid red', backgroundColor: '#ffe6e6' }}>
          <p><strong>Video Player Debug:</strong></p>
          <p>fileUrl: {String(data?.fileUrl || 'undefined')}</p>
          <p>fileUrls: {data?.fileUrls ? JSON.stringify(data.fileUrls) : 'undefined'}</p>
          <p>index: {String(index !== undefined ? index : 'undefined')}</p>
          <p>UPLOADS_URL: {UPLOADS_URL}</p>
        </div>
      );
    }
    return null;
  }

  // document.getElementsByClassName('tuby-poster')[0]?.setAttribute("alt", data?.title)
  return (
    <div 
      ref={playerRef} 
      onClick={handleClick} 
      onMouseDown={handleClick} 
      style={{ 
        display: 'block',
        width: '100%',
        position: 'relative',
        paddingBottom: '56.25%', // 16:9 aspect ratio
        height: 0,
        overflow: 'hidden',
        borderRadius: '10px',
        backgroundColor: '#000',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }}
    >
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }}>
        <Player
          pictureInPicture={true}
          keyboardShortcut={{
            pause: true,
            forward: true,
            rewind: true,
            fullScreen: true,
            mute: true,
          }}
          src={[
            {
              quality: "Full HD",
              url: url,
            },
          ]}
          poster={posterUrl || undefined}
        >
          {(ref, { onPlay, ...others }) => (
            <video
              ref={ref}
              {...others}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
              onPlay={(e) => {
                // Stop event propagation to prevent form submission
                e.stopPropagation();
                // The library original event
                onPlay && onPlay(e);
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          )}
        </Player>
      </div>
    </div>
  );
}

export default memo(VideoPlayer);
